import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { parseDate } from '@datepicker-react/hooks';
import moment from 'moment';

import { callService } from '@api/caller';
import { TourVariationViewModel } from '@api/index';
import { Guest } from '@components/ui/guest-box/guest-box-tab/GuestBox';
import {
  formatISOToDate,
  FORM_DATE_FORMAT,
  getAgeFromFormattedString,
  getCurrentDate,
  isAfterDateTime,
} from '@utils/dateUtil';

const getChildrenBirthDates = (childList: Guest['childList']) => {
  return (
    childList?.map(
      (child) => moment(child.date).add('days', 1) as unknown as string
    ) ?? []
  );
};

const getGuestsCount = (guests: Guest[]) => {
  const adultCount = guests
    ?.map((item) => item.adultNumber)
    .reduce((acc, val) => acc + val, 0);

  const childCount = guests
    ?.map((item) => item.childList?.length || 0)
    .reduce((acc, val) => acc + val, 0);

  return adultCount + childCount;
};

const validTourChildAge = (date: Date | null) => {
  let isValid = true;

  const formattedDate = formatISOToDate(String(date), FORM_DATE_FORMAT);
  const ageFromYears = getAgeFromFormattedString(
    formatISOToDate(String(date), FORM_DATE_FORMAT),
    FORM_DATE_FORMAT
  );
  const notValidAge = isAfterDateTime(
    moment(date),
    moment(
      parseDate(
        getCurrentDate().format(FORM_DATE_FORMAT),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );

  !date ||
  String(date) === '' ||
  String(formattedDate).length < 10 ||
  ageFromYears > 11 ||
  notValidAge
    ? (isValid = false)
    : (isValid = true);

  return isValid;
};

const validateSingleTourRoomChildrenAges = (room: Guest) => {
  let isValid = true;

  for (const item of room?.childList || []) {
    if (!item?.date) {
      isValid = false;
      continue;
    }

    const dateInput =
      item.date instanceof Date
        ? formatISOToDate(item.date.toISOString(), FORM_DATE_FORMAT)
        : item.date;

    if (!validTourChildAge(dateInput as unknown as Date)) {
      isValid = false;
    }
  }

  return isValid;
};

export const useGetTourVariations = (
  tourId: number,
  packageDetailId: number,
  guest: Guest,
  guestLimit?: number,
  price3?: number,
  enabled = true
) => {
  const [showGuestsWarning, setShowGuestsWarning] = useState<boolean>(false);
  const [showGuestsWarningPrice, setShowGuestsWarningPrice] =
    useState<boolean>(false);

  const { data, isError, isSuccess, isLoading, isFetching } = useQuery(
    [
      'tour-precalculate',
      tourId,
      packageDetailId,
      JSON.stringify(guest),
      guestLimit,
      price3,
    ],
    async (): Promise<TourVariationViewModel[]> => {
      if (guest.adultNumber > 2 && price3 == null) {
        setShowGuestsWarning(false);
        setShowGuestsWarningPrice(true);
        return [];
      }

      if (
        typeof guestLimit === 'number' &&
        guestLimit >= 0 &&
        getGuestsCount([guest]) > guestLimit
      ) {
        setShowGuestsWarningPrice(false);
        setShowGuestsWarning(true);
        return [];
      }

      setShowGuestsWarningPrice(false);
      setShowGuestsWarning(false);

      const isValidDates = validateSingleTourRoomChildrenAges(guest);
      if (!isValidDates) {
        return [];
      }

      const precalcResponse = await callService(
        'ToursService',
        'tourPrecalculate',
        {
          requestBody: {
            searchParameters: {
              id: tourId ?? 0,
              packageDetailId: packageDetailId ?? 0,
              guests: [
                {
                  adultCount: guest.adultNumber || 0,
                  childCount: guest.childList?.length || 0,
                  childrenBirthDates: getChildrenBirthDates(guest.childList),
                },
              ],
            },
          },
        }
      ).catch(() => undefined);

      const detailedVariations = precalcResponse?.detailedVariations;

      return detailedVariations
        ? ([...detailedVariations] as TourVariationViewModel[]).reverse()
        : [];
    },
    {
      enabled: enabled && Boolean(tourId && packageDetailId && guest),
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
    }
  );

  const tourDetailVariations = data ?? [];

  return {
    tourDetailVariations,
    showGuestsWarning,
    showGuestsWarningPrice,
    isError,
    isSuccess,
    isLoading: isLoading || isFetching,
    showEmptyVariationGuestsNote:
      tourDetailVariations.length === 0 &&
      !isLoading &&
      !isFetching &&
      !showGuestsWarning &&
      !showGuestsWarningPrice,
  };
};
